import type { VideoType } from "@/lib/room-state";

export interface YouTubeVideoItem {
  id: string;
  title: string;
  channel: string;
  thumbnail: string;
  duration: string;
  category: "trending" | "music" | "trailers" | "gaming" | "lofi" | "tech";
  views?: string;
}

// Piped API instances — free, no API key required, CORS-enabled.
// These proxy YouTube data without requiring authentication.
// If the primary instance is down, the fallbacks are tried in order.
const PIPED_INSTANCES = [
  "https://api.piped.private.coffee",
  "https://pipedapi.r4fo.com",
  "https://pipedapi.kavin.rocks",
  "https://pipedapi.leptons.xyz",
];

// Piped search is always enabled (no key needed)
export const isYouTubeSearchEnabled = true;

// Real YouTube search via Piped API (free, no key, CORS-enabled).
// Tries multiple instances for reliability.
export async function searchYouTube(query: string): Promise<YouTubeVideoItem[]> {
  let lastError: Error | null = null;

  for (const instance of PIPED_INSTANCES) {
    try {
      const url = `${instance}/search?q=${encodeURIComponent(query)}&filter=videos`;
      const res = await fetch(url, {
        signal: AbortSignal.timeout(8000),
        headers: { Accept: "application/json" },
      });

      if (!res.ok) {
        lastError = new Error(`${instance} returned ${res.status}`);
        continue;
      }

      const data = await res.json();
      const items = data.items || data.relatedStreams || [];

      if (items.length === 0) {
        lastError = new Error("No results found");
        continue;
      }

      // Extract video ID from the Piped URL format: /watch?v=VIDEO_ID
      return items
        .filter((item: any) => item.type === "stream" && item.url)
        .map((item: any) => {
          const videoId = extractVideoId(item.url);
          return {
            id: videoId,
            title: item.title || "Untitled",
            channel: item.uploaderName || "Unknown",
            // Use YouTube's thumbnail directly (more reliable than Piped's proxy)
            thumbnail: `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`,
            duration: item.duration > 0 ? formatDuration(item.duration) : "LIVE",
            views: item.views > 0 ? formatViews(item.views) : undefined,
            category: "trending" as const,
          };
        })
        .filter((v: any) => v.id);
    } catch (err) {
      lastError = err instanceof Error ? err : new Error(String(err));
      continue;
    }
  }

  throw lastError || new Error("All Piped instances failed");
}

function extractVideoId(url: string): string {
  const match = url.match(/watch\?v=([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : "";
}

function formatDuration(seconds: number): string {
  if (seconds < 0) return "LIVE";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function formatViews(views: number): string {
  if (views >= 1_000_000_000) return `${(views / 1_000_000_000).toFixed(1)}B views`;
  if (views >= 1_000_000) return `${(views / 1_000_000).toFixed(1)}M views`;
  if (views >= 1_000) return `${(views / 1_000).toFixed(1)}K views`;
  return `${views} views`;
}
